import studentService from 'ApiService/Teacher/Student';
import Card from 'components/card';
import Nodatafound from 'pages/Common/Nodatafound';
import { formatDate } from 'pages/helpers/dateUtils';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BallTriangle } from 'react-loader-spinner';

const ViewMockMarks = ({ classData, handleBack }) => {
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [dates, setDates] = useState([]);
  const [selectedDates, setSelectedDates] = useState('');
  const [totalMarks, setTotalMarks] = useState('');
  const [marks, setMarks] = useState({});

  const fetchSubjects = async () => {
    try {
      const response = await studentService.getUniqueSubjects(
        classData.courseId,
        classData.batchId
      );
      if (response.data.success) {
        setSubjects(response.data.subjects);
      }
    } catch (error) {
      toast.error('Failed to fetch subjects');
    } finally {
      setLoading(false);
    }
  };
  const fetchDates = async () => {
    try {
      const response = await studentService.getUniqueDates(selectedSubject);
      console.log(response, 'response');
      if (response.data.success) {
        setDates(response.data.tests);
      }
    } catch (error) {
      toast.error('Failed to fetch subjects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedSubject) {
      fetchDates();
    }
  }, [selectedSubject]);

  const fetchMarks = async () => {
    console.log(selectedDates, 'selectedDates');
    if (!selectedDates || !selectedSubject) return;

    try {
      setLoading(true);
      const response = await studentService.getMockMarksByDate(
        classData.courseId,
        classData.batchId,
        selectedDates,
        selectedSubject
      );

      if (response.data.success && response.data.data.length > 0) {
        const marksData = {};
        response.data.data.forEach((mark) => {
          marksData[mark.studentId] = mark.Marks;
        });
        setMarks(marksData);
        setTotalMarks(response.data.data[0].TotalMarks);

        const studentResponse = await studentService.getStudent(
          classData.courseId,
          classData.batchId
        );
        if (studentResponse.data.success) {
          const studentData = studentResponse.data.data.map((student) => ({
            id: student._id,
            name: student.name,
            instituteId: student.instituteId,
          }));
          setStudents(studentData);
        }
      } else {
        setMarks({});
        setStudents([]);
        toast.error('No marks found for selected date and subject');
      }
    } catch (error) {
      toast.error('Failed to fetch marks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (classData.courseId && classData.batchId) {
      fetchSubjects();
    }
  }, [classData]);

  useEffect(() => {
    fetchMarks();
  }, [selectedDates, selectedSubject]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <BallTriangle color="#5431ed" />
      </div>
    );
  }

  const calculateStats = () => {
    if (students.length === 0) return null;

    const marksList = students
      .map((student) => marks[student.id])
      .filter((mark) => mark !== undefined && mark !== null && mark > 0);

    if (marksList.length === 0) return null;

    // Calculate average
    const avg = marksList.reduce((a, b) => a + b, 0) / marksList.length;

    // Calculate max and min
    const max = Math.max(...marksList);
    const min = Math.min(...marksList);

    // Calculate pass percentage (marks >= 35% of total marks)
    const passThreshold = totalMarks * 0.35;
    const passCount = marksList.filter((mark) => mark >= passThreshold).length;
    const passPercentage = (passCount / marksList.length) * 100;

    return {
      avg: parseFloat(avg.toFixed(1)),
      max,
      min,
      passPercentage: parseFloat(passPercentage.toFixed(1)),
      totalStudents: marksList.length,
      passCount,
    };
  };

  const stats = calculateStats();

  return (
    <Card extra={'w-full sm:overflow-auto p-4'}>
      <div className="mb-4 flex flex-col space-y-4">
        {/* Filters */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="w-full">
            <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
              Select Subject
            </label>
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
            >
              <option value="">Select Subject</option>
              {subjects.map((subject) => (
                <option
                  className="cursor-pointer font-bold text-navy-800 dark:text-white"
                  key={subject}
                  value={subject}
                >
                  {subject}
                </option>
              ))}
            </select>
          </div>

          <div className="w-full">
            <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
              Select Test Date
            </label>
            <select
              value={selectedDates}
              onChange={(e) => setSelectedDates(e.target.value)}
              className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
            >
              <option value="">Select Date</option>
              {dates.map((date) => (
                <option
                  className="cursor-pointer font-bold text-navy-800 dark:text-white"
                  key={date._id}
                  value={date}
                >
                  {formatDate(date)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div className="rounded-lg bg-blue-50 p-4 dark:bg-navy-800">
              <h3 className="text-sm font-semibold">Average Marks</h3>
              <p className="text-lg font-bold">
                {stats.avg}/{totalMarks}
              </p>
              <p className="text-xs text-gray-600">
                {((stats.avg / totalMarks) * 100).toFixed(1)}%
              </p>
            </div>
            <div className="rounded-lg bg-green-50 p-4 dark:bg-navy-800">
              <h3 className="text-sm font-semibold">Highest Marks</h3>
              <p className="text-lg font-bold">
                {stats.max}/{totalMarks}
              </p>
              <p className="text-xs text-gray-600">
                {((stats.max / totalMarks) * 100).toFixed(1)}%
              </p>
            </div>
            <div className="rounded-lg bg-red-50 p-4 dark:bg-navy-800">
              <h3 className="text-sm font-semibold">Lowest Marks</h3>
              <p className="text-lg font-bold">
                {stats.min}/{totalMarks}
              </p>
              <p className="text-xs text-gray-600">
                {((stats.min / totalMarks) * 100).toFixed(1)}%
              </p>
            </div>
            <div className="rounded-lg bg-yellow-50 p-4 dark:bg-navy-800">
              <h3 className="text-sm font-semibold">Pass Percentage</h3>
              <p className="text-lg font-bold">{stats.passPercentage}%</p>
              <p className="text-xs text-gray-600">
                {stats.passCount} out of {stats.totalStudents} passed
              </p>
            </div>
          </div>
        )}

        {/* No Data Message */}
        {!students.length && !loading && (
          <div className="text-center text-gray-600 dark:text-gray-400">
            <Nodatafound title="No marks found for selected filters." />
          </div>
        )}

        {/* Marks Table */}
        {students.length > 0 && (
          <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-white/10">
                  <th className="px-4 py-3 text-center text-xs font-bold">
                    SR.NO
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold">
                    NAME
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-bold">
                    MARKS
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-bold">
                    PERCENTAGE
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-bold">
                    STATUS
                  </th>
                </tr>
              </thead>
              <tbody>
                {students.map((student, index) => {
                  const studentMarks = marks[student.id] || 0;
                  const percentage = (studentMarks / totalMarks) * 100;

                  let status = 'Pending';
                  let statusClass =
                    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';

                  if (studentMarks > 0) {
                    status = percentage >= 35 ? 'Pass' : 'Fail';
                    statusClass =
                      status === 'Pass'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
                  }

                  return (
                    <tr
                      key={student.id}
                      className="border-b border-gray-200 dark:border-white/10"
                    >
                      <td className="px-4 py-3 text-center">{index + 1}</td>
                      <td className="px-4 py-3">{student.name}</td>
                      <td className="px-4 py-3 text-center">
                        {studentMarks}/{totalMarks}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {percentage.toFixed(1)}%
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`rounded-full px-2 py-1 text-xs ${statusClass}`}
                        >
                          {status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Back Button */}
        <div className="flex justify-start pt-4">
          <button
            onClick={handleBack}
            className="cursor-pointer rounded-xl bg-lightPrimary px-16 py-2 font-medium text-navy-700 transition duration-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10 dark:active:bg-white/20"
          >
            Back
          </button>
        </div>
      </div>
    </Card>
  );
};

export default ViewMockMarks;
